import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import api from '../../../api.js';

function TripFormModal({ isOpen, onClose, onSuccess, onMessage, editingTrip, vehicles, drivers }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    vehicle_id: '',
    driver_id: '',
    start_date: '',
    end_date: '',
    start_mileage: '',
    end_mileage: '',
    destination: '',
    purpose: '',
    status: 'planned',
    notes: '',
  });

  useEffect(() => {
    if (editingTrip) {
      setFormData({
        vehicle_id: editingTrip.vehicle_id || '',
        driver_id: editingTrip.driver_id || '',
        start_date: editingTrip.start_date || '',
        end_date: editingTrip.end_date || '',
        start_mileage: editingTrip.start_mileage || '',
        end_mileage: editingTrip.end_mileage || '',
        destination: editingTrip.destination || '',
        purpose: editingTrip.purpose || '',
        status: editingTrip.status || 'planned',
        notes: editingTrip.notes || '',
      });
    } else {
      // Reset form for new trip
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        vehicle_id: '',
        driver_id: '',
        start_date: today,
        end_date: '',
        start_mileage: '',
        end_mileage: '',
        destination: '',
        purpose: '',
        status: 'planned',
        notes: '',
      });
    }
  }, [editingTrip]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };

      // Convert numeric fields
      if (submitData.vehicle_id) submitData.vehicle_id = parseInt(submitData.vehicle_id, 10);
      if (submitData.driver_id) submitData.driver_id = parseInt(submitData.driver_id, 10);
      if (submitData.start_mileage) submitData.start_mileage = parseInt(submitData.start_mileage, 10);
      if (submitData.end_mileage) submitData.end_mileage = parseInt(submitData.end_mileage, 10);

      // Clean up empty fields
      if (!submitData.driver_id) submitData.driver_id = null;
      if (!submitData.end_date) delete submitData.end_date;
      if (!submitData.start_mileage) delete submitData.start_mileage;
      if (!submitData.end_mileage) delete submitData.end_mileage;
      if (!submitData.destination) delete submitData.destination;
      if (!submitData.purpose) delete submitData.purpose;
      if (!submitData.notes) delete submitData.notes;

      if (editingTrip) {
        await api.trip.update(editingTrip.id, submitData);
        onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.tripForm.successUpdate'), type: 'success' });
      } else {
        await api.trip.create(submitData);
        onMessage && onMessage({ title: t('common.messages.successTitle'), message: t('settings.tripForm.successCreate'), type: 'success' });
      }
      onSuccess();
    } catch (error) {
      onMessage && onMessage({ title: t('common.messages.errorTitle'), message: t('settings.tripForm.errorPrefix', { message: error.message }), type: 'error' });
    }
  };

  // Get current mileage from selected vehicle
  const getVehicleMileage = () => {
    if (!formData.vehicle_id) return null;
    const vehicle = vehicles.find(v => v.id === parseInt(formData.vehicle_id, 10));
    return vehicle?.mileage || null;
  };

  if (!isOpen) return null;

  const vehicleMileage = getVehicleMileage();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {editingTrip ? t('settings.tripForm.editTitle') : t('settings.tripForm.createTitle')}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Vehicle and Driver */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.tripForm.vehicleLabel')}</label>
              <select
                required
                value={formData.vehicle_id}
                onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="">{t('settings.tripForm.vehicleSelectPlaceholder')}</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.make} {vehicle.model} - {vehicle.license_plate}
                  </option>
                ))}
              </select>
              {vehicleMileage && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('settings.tripForm.currentMileage', { mileage: vehicleMileage.toLocaleString() })}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.tripForm.driverLabel')}</label>
              <select
                value={formData.driver_id}
                onChange={(e) => setFormData({ ...formData, driver_id: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              >
                <option value="">{t('settings.tripForm.driverNoneOption')}</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.first_name} {driver.last_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.tripForm.startDateLabel')}</label>
              <input
                type="date"
                required
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.tripForm.endDateLabel')}</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                min={formData.start_date}
              />
            </div>
          </div>

          {/* Mileage */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.tripForm.startMileageLabel')}</label>
              <input
                type="number"
                value={formData.start_mileage}
                onChange={(e) => setFormData({ ...formData, start_mileage: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder={vehicleMileage ? vehicleMileage.toString() : 'km'}
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.tripForm.endMileageLabel')}</label>
              <input
                type="number"
                value={formData.end_mileage}
                onChange={(e) => setFormData({ ...formData, end_mileage: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder="km"
                min={formData.start_mileage || 0}
              />
              {formData.start_mileage && formData.end_mileage && (
                <p className="text-xs text-green-600 mt-1">
                  {t('settings.tripForm.distanceHint', { distance: (parseInt(formData.end_mileage) - parseInt(formData.start_mileage)).toLocaleString() })}
                </p>
              )}
            </div>
          </div>

          {/* Destination */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.tripForm.destinationLabel')}</label>
            <input
              type="text"
              value={formData.destination}
              onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder={t('settings.tripForm.destinationPlaceholder')}
            />
          </div>

          {/* Purpose */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.tripForm.purposeLabel')}</label>
            <select
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="">{t('settings.tripForm.purposeSelectPlaceholder')}</option>
              <option value="business">{t('settings.tripForm.purposes.business')}</option>
              <option value="delivery">{t('settings.tripForm.purposes.delivery')}</option>
              <option value="client_visit">{t('settings.tripForm.purposes.client_visit')}</option>
              <option value="maintenance">{t('settings.tripForm.purposes.maintenance')}</option>
              <option value="training">{t('settings.tripForm.purposes.training')}</option>
              <option value="other">{t('settings.tripForm.purposes.other')}</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.tripForm.statusLabel')}</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="planned">{t('settings.tripForm.tripStatuses.planned')}</option>
              <option value="in_progress">{t('settings.tripForm.tripStatuses.in_progress')}</option>
              <option value="completed">{t('settings.tripForm.tripStatuses.completed')}</option>
              <option value="cancelled">{t('settings.tripForm.tripStatuses.cancelled')}</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">{t('settings.tripForm.notesLabel')}</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
              rows={3}
              placeholder={t('settings.tripForm.notesPlaceholder')}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-semibold transition"
            >
              {editingTrip ? t('common.actions.update') : t('common.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TripFormModal;
